import * as Y from "yjs";
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from "y-protocols/awareness";
import { instancePath } from "../../lib/instance.ts";

export type Participant = { id: string; name: string; color: string; editing: boolean; clients: number };
export type CollaborationState = { connection: "connecting" | "connected" | "disconnected"; pending: number; revision: number; problem: "" | "restored" | "readonly" | "invalid"; participants: Participant[] };
export function userColor(id: string) {
  const sum = (id.replaceAll("-", "").match(/../g) ?? []).reduce((n, b) => n + parseInt(b, 16), 0);
  return ["#7c83ff", "#e5975b", "#42bda3", "#d879b6", "#6ca8ed", "#b398e8"][sum % 6]!;
}

/** Pending changes stay in the Y.Doc until the server acknowledges durable storage. */
export class TernProvider {
  readonly doc: Y.Doc;
  readonly awareness: Awareness;
  private ws: WebSocket | null = null;
  private retry = 0;
  private closed = false;
  private ready = false;
  private epoch: number | null = null;
  private sequence = 0;
  private pending = new Map<number, Uint8Array>();
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private activityTimer: ReturnType<typeof setTimeout> | undefined;
  private listeners = new Set<(s: string) => void>();
  private stateListeners = new Set<(s: CollaborationState) => void>();
  private syncedListeners = new Set<() => void>();
  private synced = false;
  private state: CollaborationState = { connection: "connecting", pending: 0, revision: 0, problem: "", participants: [] };

  readonly docId: string;
  constructor(docId: string, doc?: Y.Doc) {
    this.docId = docId;
    this.doc = doc ?? new Y.Doc();
    this.awareness = new Awareness(this.doc);
    this.doc.on("update", this.onDocUpdate);
    this.awareness.on("update", this.onAwareness);
    window.addEventListener("beforeunload", this.beforeUnload);
    window.addEventListener("blur", this.onBlur);
    this.connect();
  }
  get isSynced() { return this.synced; }
  onStatus(fn: (s: string) => void) { this.listeners.add(fn); fn(this.state.connection); return () => { this.listeners.delete(fn); }; }
  onState(fn: (s: CollaborationState) => void) { this.stateListeners.add(fn); fn(this.state); return () => { this.stateListeners.delete(fn); }; }
  onceSynced(fn: () => void) { if (this.synced) fn(); else this.syncedListeners.add(fn); return () => { this.syncedListeners.delete(fn); }; }
  private emit(patch: Partial<CollaborationState> = {}) {
    this.state = { ...this.state, ...patch, pending: this.pending.size };
    this.listeners.forEach((l) => l(this.state.connection));
    this.stateListeners.forEach((l) => l(this.state));
  }
  activity() {
    this.awareness.setLocalStateField("editing", true);
    clearTimeout(this.activityTimer);
    this.activityTimer = setTimeout(() => this.awareness.setLocalStateField("editing", false), 2500);
  }
  private onBlur = () => { this.awareness.setLocalStateField("editing", false); };
  private connect() {
    if (this.closed || this.state.problem) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${location.host}${instancePath}/api/docs/${this.docId}/collab`);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.ready = false;
    this.emit({ connection: "connecting" });
    ws.onopen = () => {
      this.retry = 0;
      this.emit({ connection: "connected" });
      this.send(1, encodeAwarenessUpdate(this.awareness, [this.doc.clientID]));
    };
    ws.onmessage = (message) => {
      const bytes = new Uint8Array(message.data as ArrayBuffer);
      const payload = bytes.subarray(1);
      if (bytes[0] === 3) {
        const data = JSON.parse(new TextDecoder().decode(payload)) as { type: string; epoch?: number; sequence?: number; revision?: number; code?: string };
        if (data.type === "hello") {
          if (this.epoch !== null && this.epoch !== data.epoch) { this.changed(); return; }
          this.epoch = data.epoch!;
          this.emit({ revision: data.revision! });
        } else if (data.type === "saved") {
          this.pending.delete(data.sequence!);
          this.emit({ revision: Math.max(this.state.revision, data.revision!) });
        } else if (data.type === "reset") this.changed();
        else if (data.type === "error" && data.code !== "retry") {
          this.emit({ problem: data.code === "stale" ? "restored" : data.code === "readonly" ? "readonly" : "invalid" });
        }
      } else if (bytes[0] === 0 && !this.state.problem) {
        Y.applyUpdate(this.doc, payload, this);
        if (!this.ready) {
          this.ready = true;
          for (const [seq, update] of this.pending) this.sendUpdate(seq, update);
          if (!this.synced) {
            this.synced = true;
            this.syncedListeners.forEach((f) => f());
            this.syncedListeners.clear();
          }
        }
      } else if (bytes[0] === 1) applyAwarenessUpdate(this.awareness, payload, this);
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.ready = false;
      removeAwarenessStates(this.awareness, [...this.awareness.getStates().keys()].filter((id) => id !== this.doc.clientID), this);
      this.emit({ connection: "disconnected" });
      if (this.closed || this.state.problem) return;
      this.retryTimer = setTimeout(() => this.connect(), Math.min(15_000, 500 * 2 ** Math.min(this.retry++, 5)));
    };
    ws.onerror = () => ws.close();
  }
  private changed() {
    this.emit({ problem: "restored" });
    window.dispatchEvent(new CustomEvent("tern:doc-restored", { detail: { id: this.docId, pending: this.pending.size } }));
  }
  private send(kind: number, payload: Uint8Array) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const bytes = new Uint8Array(payload.length + 1); bytes[0] = kind; bytes.set(payload, 1); this.ws.send(bytes);
  }
  private sendUpdate(sequence: number, update: Uint8Array) {
    if (!this.ready || this.epoch === null || this.state.problem) return;
    const payload = new Uint8Array(update.length + 8);
    const view = new DataView(payload.buffer); view.setUint32(0, this.epoch, true); view.setUint32(4, sequence, true);
    payload.set(update, 8); this.send(0, payload);
  }
  private onDocUpdate = (update: Uint8Array, origin: unknown) => {
    if (origin === this) return;
    const sequence = ++this.sequence;
    this.pending.set(sequence, update);
    this.sendUpdate(sequence, update);
    this.activity(); this.emit();
  };
  private onAwareness = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => {
    if (origin !== this && [...added, ...updated, ...removed].includes(this.doc.clientID)) this.send(1, encodeAwarenessUpdate(this.awareness, [this.doc.clientID]));
    const participants = new Map<string, Participant>();
    this.awareness.getStates().forEach((state) => {
      const user = state.user as { id?: string; name?: string; color?: string } | undefined;
      if (!user?.id || !user.name) return;
      const prior = participants.get(user.id);
      participants.set(user.id, { id: user.id, name: user.name, color: user.color ?? userColor(user.id), editing: !!state.editing || !!prior?.editing, clients: (prior?.clients ?? 0) + 1 });
    });
    this.emit({ participants: [...participants.values()].sort((a, b) => a.id.localeCompare(b.id)) });
  };
  private beforeUnload = (event: BeforeUnloadEvent) => {
    if (this.pending.size) { event.preventDefault(); event.returnValue = ""; }
    else removeAwarenessStates(this.awareness, [this.doc.clientID], "unload");
  };
  destroy() {
    this.closed = true; clearTimeout(this.retryTimer); clearTimeout(this.activityTimer);
    removeAwarenessStates(this.awareness, [this.doc.clientID], "destroy");
    this.doc.off("update", this.onDocUpdate); this.awareness.off("update", this.onAwareness);
    window.removeEventListener("beforeunload", this.beforeUnload); window.removeEventListener("blur", this.onBlur);
    this.ws?.close(); this.awareness.destroy(); this.listeners.clear(); this.stateListeners.clear(); this.syncedListeners.clear();
  }
}
